import { useEffect, useRef, useState } from 'react';
import { ChevronDown } from 'lucide-react';
import { Button } from '../ui';
import type { StudentSeatMenuItem } from './StudentSeatContextMenu';

type StudentSeatGroupActionsMenuProps = {
  selectedCount: number;
  items: StudentSeatMenuItem[];
  disabled?: boolean;
};

export default function StudentSeatGroupActionsMenu({
  selectedCount,
  items,
  disabled = false
}: StudentSeatGroupActionsMenuProps) {
  const [open, setOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointerDown = (event: MouseEvent) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      setOpen(false);
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false);
    };
    window.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const isDisabled = disabled || selectedCount === 0;

  return (
    <div ref={menuRef} className="relative">
      <Button
        type="button"
        size="sm"
        variant="outline"
        disabled={isDisabled}
        onClick={() => setOpen((prev) => !prev)}
        className="inline-flex items-center gap-1.5"
      >
        Gruppenaktionen
        {selectedCount > 0 ? ` (${selectedCount})` : ''}
        <ChevronDown className="h-3.5 w-3.5" />
      </Button>
      {open && !isDisabled ? (
        <div className="absolute left-0 top-full z-20 mt-1 min-w-[220px] rounded-lg border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900">
          <div className="border-b border-gray-100 px-3 py-2 text-xs font-medium text-gray-500 dark:border-gray-800 dark:text-gray-400">
            {selectedCount} Schüler ausgewählt
          </div>
          {items.map((item) => (
            <button
              key={item.id}
              type="button"
              disabled={item.disabled}
              className={`block w-full px-3 py-2.5 text-left text-sm transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                item.destructive
                  ? 'text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/40'
                  : 'text-gray-800 hover:bg-gray-50 dark:text-gray-100 dark:hover:bg-gray-800'
              }`}
              onClick={() => {
                if (item.disabled) return;
                item.onClick();
                setOpen(false);
              }}
            >
              {item.label}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
